import type { ApprovalLineDetail } from '../types/approval';
import { referrerLines } from './approval-line-slot';

/**
 * **임시보관 상신 본문** — `POST /approval/documents/:id/submit` (2026-09-13 · 계약 §13.3).
 *
 * ## ⛔ 2026-09-27 — 합의 칸(`agreerIds`)을 **지웠다**
 *
 * 사용자 지시 「전자결재에서 합의와 관련된 기능은 다 삭제해라」. 서버도 같은 날 그 길을 닫았다(#1774).
 * 종전에는 이 파일이 「생략 = 보존 · `[]` = 비움 · 목록 = 교체」 셋을 가려 실었는데, **보낼 칸이
 * 없어져** 그 셈이 통째로 사라졌다.
 *
 * ```
 * 근거  ① 운영 AGREE 0        2026-09-26 8차 배포에서 배포서버가 실측
 *       ② 새 문서는 «구조상» 안 만든다   서버 planApprovalLines 가 결재자로 합친다(#1774)
 * ```
 *
 * ## ⛔⛔ 그래도 아래 **거르개는 남는다** — 그것은 «합의 기능»이 아니다
 *
 * `approverIds` 를 만드는 `.filter(lineType === 'APPROVE')` 는 **결재 안전장치**다.
 * 까닭은 그 자리에 적혀 있고, 시험이 잠근다. 낱말에 「합의」가 있다고 함께 지우지 말 것.
 */

type Line = Pick<ApprovalLineDetail, 'lineType' | 'approverId'> & {
  approver?: { userId: string | number } | null;
};

const userIdOf = (l: Line): string => String(l.approver?.userId ?? l.approverId);

export interface DraftSubmitBody {
  approverIds: string[];
  referrerIds?: string[];
}

/**
 * DRAFT 의 결재선 → 상신 본문.
 *
 * ★ `approverIds` 가 비어 있어도 그대로 돌려준다 — 「결재자가 없습니다」 를 **어떻게 말할지**는 부르는 화면의 몫이다.
 */
export function buildDraftSubmitBody(lines: ReadonlyArray<Line>): DraftSubmitBody {
  /**
   * ⛔⛔ **이 거르개는 «합의 기능»이 아니라 «결재 안전장치»다** — 지우지 말 것 (2026-09-27).
   *
   * 이름 때문에 「합의를 다 지웠으니 이것도 필요 없다」로 읽히기 쉽다. 아니다. 이것은
   * **`approverIds` 에 «결재자만» 담기게** 하는 «양성 거르개»이고, 빼면 **결재선의 다른 줄이
   * 결재자로 섞인다.** 줄은 step 순이라 섞인 사람이 **맨 뒤 = 최종결재자**가 된다.
   *
   * ```
   * ⓐ AGREE 가 섞이면   실측된 결함이다 — 문서 84 에서 {"approverIds":["5","1"]} 로 나가
   *                    totalSteps 가 1 → 2 가 됐다(합의자가 최종결재자가 됐다)
   * ⓑ REFER 가 섞이면   ⭐ 더 나쁘다 — 참고자는 «살아 있다»(운영 13줄).
   *                    보기만 하던 사람이 «문서를 멈추는 사람»이 된다
   * ```
   * ⇒ ⓐ 는 합의를 다 지운 뒤 «생길 일이 없어» 보이지만, ⓑ 는 **지금도 매일 일어날 수 있다.**
   *   그래서 이 줄은 합의와 «무관하게» 남는다.
   * ★ `approval-submit-body.test.ts` 의 「결재자 목록에는 결재 줄만」 시험이 이것을 잠근다 —
   *   그 시험이 빨개지면 **이 줄을 건드린 것**이다.
   */
  const approverIds = lines
    .filter((l) => (l.lineType ?? 'APPROVE') === 'APPROVE')
    .map(userIdOf);
  const referrerIds = referrerLines(lines).map(userIdOf);
  return {
    approverIds,
    ...(referrerIds.length > 0 ? { referrerIds } : {}),
  };
}
